import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { IMAGES } from "../config";
import SEO from "../components/SEO";
import { useBookingModal } from "../context/BookingModalContext";

const privateHireSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "Corporate Event Bar Hire",
  "provider": { "@id": "https://www.barspotltd.co.uk/#business" },
  "name": "Corporate Event & Private Party Bar Hire",
  "description": "Professional mobile bar hire for corporate events, birthdays, garden parties and private celebrations across Birmingham, Cambridge and the UK.",
  "areaServed": [
    { "@type": "City", "name": "Birmingham" },
    { "@type": "City", "name": "Cambridge" },
    { "@type": "AdministrativeArea", "name": "West Midlands" },
    { "@type": "Country", "name": "United Kingdom" }
  ]
};

const PrivateHirePage = () => {
  const { openBookingModal } = useBookingModal();

  return (
    <div className="pt-20">
      <SEO
        title="Corporate Event Bar Hire & Private Parties | Bar Spot"
        description="Professional mobile bar hire for corporate events, birthdays & private parties. Flexible packages, premium service. Birmingham, Cambridge & UK-wide."
        canonical="/private-hire"
        schema={privateHireSchema}
      />
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.friends} alt="Mobile bar hire for private party with friends celebrating" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#fafafa]/95 via-[#fafafa]/80 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <p className="text-[#a5dae0] font-script text-2xl mb-4">Private & Corporate</p>
            <h1 className="text-5xl md:text-6xl mb-6">Corporate Event Bar Hire</h1>
            <p className="text-lg text-[#666666] leading-relaxed mb-8">
              Professional mobile bar services for birthdays, corporate functions, garden parties, and private celebrations.
              Tailored to your needs, delivered with style across Birmingham, Cambridge and the UK.
            </p>
            <button
              onClick={openBookingModal}
              data-testid="private-cta"
              className="bg-[#a5dae0] text-[#1a1a1a] px-8 py-4 rounded-full font-semibold hover:bg-[#8bc9d0] transition-all inline-flex items-center gap-2"
            >
              Get Quote <ArrowRight size={20} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Cross-links to other services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-[#555555] mb-6">Explore our other mobile bar hire services</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/weddings" className="px-6 py-3 rounded-full border-2 border-gray-200 text-[#444444] hover:border-[#a5dae0] hover:text-[#a5dae0] transition-all text-sm font-medium">Wedding Bar Hire</Link>
            <Link to="/festivals" className="px-6 py-3 rounded-full border-2 border-gray-200 text-[#444444] hover:border-[#a5dae0] hover:text-[#a5dae0] transition-all text-sm font-medium">Festivals & Large Events</Link>
            <Link to="/menu" className="px-6 py-3 rounded-full border-2 border-gray-200 text-[#444444] hover:border-[#a5dae0] hover:text-[#a5dae0] transition-all text-sm font-medium">View Drinks Menu</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivateHirePage;
